import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  TextField,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { styled } from '@mui/material/styles';
import {
  Activity,
  BarChart3 as BarChartLogoIcon,
  CircleDollarSign,
  Database,
  Gauge,
  ShieldAlert,
  ShieldCheck,
  TrendingUp,
  Users
} from 'lucide-react';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from 'recharts';
import { toast } from 'react-toastify';
import {
  getFunnelixContatti,
  getFunnelixContattiDistincts,
  getFunnelixContattiPipelineSummary,
  getFunnelixContattiStageSummary,
  getFunnelixKpi,
  getFunnelixLogSync,
  getLastSuccessfulImportFunnelixContatti
} from '../services/funnelixApi';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  height: '100%'
}));

const MetricTitle = ({ title, tooltip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
    <Typography variant="body2" color="text.secondary">
      {title}
    </Typography>
    {tooltip ? (
      <Tooltip
        title={
          <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
            {tooltip}
          </Typography>
        }
        arrow
      >
        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    ) : null}
  </Box>
);

const KpiCard = ({ title, value, subtitle, tooltip, icon: Icon, iconBg, iconColor }) => (
  <StyledCard>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <MetricTitle title={title} tooltip={tooltip} />
        {Icon ? (
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: iconBg || '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Icon size={18} color={iconColor || '#4f46e5'} />
          </Box>
        ) : null}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    </CardContent>
  </StyledCard>
);

const formatNumber = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('it-IT');
};

const formatPercent = (value, decimals = 1) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0%';
  return `${n.toFixed(decimals).replace('.', ',')}%`;
};

const formatCurrency = (value, currency = 'EUR') => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '-';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2
  }).format(n);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('it-IT');
};

const formatRelativeAge = (hoursValue) => {
  const hours = Number(hoursValue);
  if (!Number.isFinite(hours) || hours < 0) return '-';
  const minutes = Math.round(hours * 60);
  if (minutes < 1) return 'meno di 1 minuto';
  if (minutes < 60) return `${minutes} minuti`;
  if (hours < 24) return `${hours.toFixed(1).replace('.', ',')} ore`;
  const days = Math.floor(hours / 24);
  return `${days} giorno${days > 1 ? 'i' : ''}`;
};

const getFreshnessTone = (hoursValue) => {
  const hours = Number(hoursValue);
  if (!Number.isFinite(hours) || hours < 0) {
    return {
      color: 'text.secondary',
      bg: 'transparent'
    };
  }
  if (hours < 2) {
    return {
      color: '#166534',
      bg: '#ecfdf3'
    };
  }
  if (hours < 12) {
    return {
      color: '#92400e',
      bg: '#fffbeb'
    };
  }
  return {
    color: '#b91c1c',
    bg: '#fef2f2'
  };
};

const formatYmdToIt = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).split('-');
  if (!y || !m || !d) return String(ymd);
  return `${d}/${m}/${y}`;
};

const readKpiValue = (rows = []) => {
  const value = Number(rows?.[0]?.value || 0);
  return Number.isFinite(value) ? value : 0;
};

const toYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getDefaultDateFrom = () => {
  const today = new Date();
  return `${today.getFullYear()}-01-01`;
};

const getDefaultDateTo = () => toYmd(new Date());

const FunnelixKPI = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('operativi');
  const [trendMode, setTrendMode] = useState('all');
  const [snapshot, setSnapshot] = useState(null);
  const [pipelineOptions, setPipelineOptions] = useState([]);
  const [filters, setFilters] = useState({
    data_inserimento_dal: getDefaultDateFrom(),
    data_inserimento_al: getDefaultDateTo(),
    pipeline: ''
  });

  const sharedFilters = useMemo(() => ({
    data_inserimento_dal: filters.data_inserimento_dal || undefined,
    data_inserimento_al: filters.data_inserimento_al || undefined,
    pipeline: filters.pipeline || undefined
  }), [filters.data_inserimento_al, filters.data_inserimento_dal, filters.pipeline]);

  const loadPipelineOptions = useCallback(async () => {
    try {
      const response = await getFunnelixContattiDistincts({
        data_inserimento_dal: filters.data_inserimento_dal || undefined,
        data_inserimento_al: filters.data_inserimento_al || undefined
      });
      const items = Array.isArray(response?.pipelines) ? response.pipelines : [];
      const normalized = items
        .map((item) => {
          const id = item?.funnelix_pipeline_id || item?.pipeline_id || item?.id || '';
          if (!id) return null;
          return {
            id: String(id),
            nome: item?.nome || item?.name || String(id)
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.nome.localeCompare(b.nome, 'it', { sensitivity: 'base' }));
      setPipelineOptions(normalized);
      if (filters.pipeline && !normalized.some((item) => item.id === String(filters.pipeline))) {
        setFilters((prev) => ({ ...prev, pipeline: '' }));
      }
    } catch (_error) {
      setPipelineOptions([]);
    }
  }, [filters.data_inserimento_al, filters.data_inserimento_dal, filters.pipeline]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        contattiAll,
        contattiClienti,
        contattiNonClienti,
        contattiDaVerificare,
        contattiErrore,
        pipelineSummaryAll,
        pipelineSummaryClienti,
        stageSummaryAll,
        stageSummaryClienti,
        logsSync,
        lastSuccessfulImport,
        conversionRows,
        fatturatoClientiRows,
        ticketMedioRows,
        annualImportRows,
        annualConversionRows,
        annualRevenueRows
      ] = await Promise.all([
        getFunnelixContatti({ page: 1, limit: 1, ...sharedFilters }),
        getFunnelixContatti({ page: 1, limit: 1, cliente_trovato: '1', ...sharedFilters }),
        getFunnelixContatti({ page: 1, limit: 1, cliente_trovato: '0', ...sharedFilters }),
        getFunnelixContatti({ page: 1, limit: 1, stato_verifica_cliente: 'da_verificare', ...sharedFilters }),
        getFunnelixContatti({ page: 1, limit: 1, stato_verifica_cliente: 'errore', ...sharedFilters }),
        getFunnelixContattiPipelineSummary(sharedFilters),
        getFunnelixContattiPipelineSummary({ ...sharedFilters, cliente_trovato: '1' }),
        getFunnelixContattiStageSummary(sharedFilters),
        getFunnelixContattiStageSummary({ ...sharedFilters, cliente_trovato: '1' }),
        getFunnelixLogSync({ page: 1, limit: 200 }),
        getLastSuccessfulImportFunnelixContatti(),
        getFunnelixKpi({
          period: 'last_12_months',
          metric: 'lead_to_client_conversion',
          group_by: 'total',
          ...sharedFilters
        }),
        getFunnelixKpi({
          period: 'last_12_months',
          metric: 'revenue',
          group_by: 'total',
          stato_verifica_cliente: 'verificato_cliente',
          ...sharedFilters
        }),
        getFunnelixKpi({
          period: 'last_12_months',
          metric: 'avg_opportunity_value',
          group_by: 'total',
          stato_verifica_cliente: 'verificato_cliente',
          ...sharedFilters
        }),
        getFunnelixKpi({
          period: 'last_12_months',
          metric: 'imported_count',
          group_by: 'month',
          ...sharedFilters
        }),
        getFunnelixKpi({
          period: 'last_12_months',
          metric: 'lead_to_client_conversion',
          group_by: 'month',
          ...sharedFilters
        }),
        getFunnelixKpi({
          period: 'last_12_months',
          metric: 'revenue',
          group_by: 'month',
          stato_verifica_cliente: 'verificato_cliente',
          ...sharedFilters
        })
      ]);

      const totaleContatti = Number(contattiAll?.total || 0);
      const totaleClienti = Number(contattiClienti?.total || 0);
      const totaleNonClienti = Number(contattiNonClienti?.total || 0);
      const totaleDaVerificare = Number(contattiDaVerificare?.total || 0);
      const totaleErrore = Number(contattiErrore?.total || 0);

      const pipelineItems = Array.isArray(pipelineSummaryAll?.items) ? pipelineSummaryAll.items : [];
      const stageItems = Array.isArray(stageSummaryAll?.items) ? stageSummaryAll.items : [];
      const pipelineClientiItems = Array.isArray(pipelineSummaryClienti?.items)
        ? pipelineSummaryClienti.items
        : [];
      const stageClientiItems = Array.isArray(stageSummaryClienti?.items) ? stageSummaryClienti.items : [];

      const totalePipelineMissing = pipelineItems
        .filter((item) => !item?.funnelix_pipeline_id)
        .reduce((acc, item) => acc + Number(item?.totale || 0), 0);
      const totaleStageMissing = stageItems
        .filter((item) => !item?.funnelix_stage_id)
        .reduce((acc, item) => acc + Number(item?.totale || 0), 0);

      const logs = Array.isArray(logsSync?.data) ? logsSync.data : [];
      const syncOk = logs.filter((l) => String(l?.esito || '').toLowerCase() === 'ok').length;
      const syncKo = logs.filter((l) => String(l?.esito || '').toLowerCase() !== 'ok').length;
      const syncTotale = syncOk + syncKo;

      const lastImportAt = lastSuccessfulImport?.last_successful_import_at || null;
      const freshnessHours = lastImportAt
        ? (Date.now() - new Date(lastImportAt).getTime()) / (1000 * 60 * 60)
        : null;

      const matchRate = totaleContatti > 0 ? (totaleClienti / totaleContatti) * 100 : 0;
      const nonClientRate = totaleContatti > 0 ? (totaleNonClienti / totaleContatti) * 100 : 0;

      const stageClientiMap = new Map(
        stageClientiItems.map((item) => [String(item?.funnelix_stage_id || '__NONE__'), Number(item?.totale || 0)])
      );
      const quotaClientiPerStage = stageItems
        .map((item) => {
          const key = String(item?.funnelix_stage_id || '__NONE__');
          const totale = Number(item?.totale || 0);
          const clienti = stageClientiMap.get(key) || 0;
          return {
            nome: item?.nome || 'Senza stage',
            totale,
            clienti,
            quota: totale > 0 ? (clienti / totale) * 100 : 0
          };
        })
        .filter((item) => item.totale > 0)
        .sort((a, b) => b.totale - a.totale)
        .slice(0, 8);

      const topPipeline = [...pipelineItems]
        .sort((a, b) => Number(b?.totale || 0) - Number(a?.totale || 0))
        .slice(0, 8);
      const topPipelineClienti = [...pipelineClientiItems]
        .sort((a, b) => Number(b?.totale || 0) - Number(a?.totale || 0))
        .slice(0, 8);

      const monthMap = new Map();
      const registerMonth = (monthKey) => {
        if (!monthMap.has(monthKey)) {
          const [year, month] = String(monthKey || '').split('-');
          const d = new Date(Number(year), Number(month) - 1, 1);
          const monthLabel = Number.isNaN(d.getTime())
            ? String(monthKey)
            : new Intl.DateTimeFormat('it-IT', { month: 'short', year: '2-digit' }).format(d);
          monthMap.set(monthKey, {
            month: monthKey,
            monthLabel: String(monthLabel).replace('.', ''),
            imported: 0,
            conversion: 0,
            revenue: 0,
            revenueK: 0
          });
        }
      };
      const last12Months = [];
      for (let i = 11; i >= 0; i -= 1) {
        const d = new Date();
        d.setDate(1);
        d.setMonth(d.getMonth() - i);
        const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        last12Months.push(month);
        registerMonth(month);
      }
      (annualImportRows || []).forEach((row) => {
        const month = String(row?.date || '');
        if (!month) return;
        registerMonth(month);
        monthMap.get(month).imported = Number(row?.value || 0);
      });
      (annualConversionRows || []).forEach((row) => {
        const month = String(row?.date || '');
        if (!month) return;
        registerMonth(month);
        monthMap.get(month).conversion = Number(row?.value || 0);
      });
      (annualRevenueRows || []).forEach((row) => {
        const month = String(row?.date || '');
        if (!month) return;
        registerMonth(month);
        const revenue = Number(row?.value || 0);
        monthMap.get(month).revenue = revenue;
        monthMap.get(month).revenueK = revenue / 1000;
      });
      const annualTrend = last12Months.map((month) => monthMap.get(month)).filter(Boolean);
      const annualPeakImports = annualTrend.reduce((acc, row) => Math.max(acc, Number(row?.imported || 0)), 0);
      const annualPeakRevenue = annualTrend.reduce((acc, row) => Math.max(acc, Number(row?.revenue || 0)), 0);
      const annualAvgConversion = annualTrend.length > 0
        ? annualTrend.reduce((acc, row) => acc + Number(row?.conversion || 0), 0) / annualTrend.length
        : 0;
      const annualHasAnyData = annualTrend.some((row) =>
        Number(row?.imported || 0) > 0 ||
        Number(row?.conversion || 0) > 0 ||
        Number(row?.revenue || 0) > 0
      );

      setSnapshot({
        totals: {
          totaleContatti,
          totaleClienti,
          totaleNonClienti,
          totaleDaVerificare,
          totaleErrore
        },
        operativi: {
          lastImportAt,
          freshnessHours,
          syncOk,
          syncKo,
          syncTotale,
          syncSuccessRate: syncTotale > 0 ? (syncOk / syncTotale) * 100 : 0,
          syncErrorRate: syncTotale > 0 ? (syncKo / syncTotale) * 100 : 0
        },
        qualita: {
          totalePipelineMissing,
          totaleStageMissing,
          coperturaPipeline: totaleContatti > 0 ? ((totaleContatti - totalePipelineMissing) / totaleContatti) * 100 : 0,
          coperturaStage: totaleContatti > 0 ? ((totaleContatti - totaleStageMissing) / totaleContatti) * 100 : 0
        },
        verifica: {
          matchRate,
          nonClientRate,
          conversionLeadCliente: readKpiValue(conversionRows),
          fatturatoClienti: readKpiValue(fatturatoClientiRows),
          ticketMedioClienti: readKpiValue(ticketMedioRows)
        },
        funnel: {
          topPipeline,
          topPipelineClienti,
          quotaClientiPerStage
        },
        annual: {
          annualTrend,
          annualPeakImports,
          annualPeakRevenue,
          annualAvgConversion,
          annualHasAnyData
        }
      });
    } catch (error) {
      console.error('Errore caricamento KPI Funnelix:', error);
      toast.error('Errore nel caricamento KPI Funnelix');
    } finally {
      setLoading(false);
    }
  }, [sharedFilters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadPipelineOptions();
  }, [loadPipelineOptions]);

  const tabs = useMemo(
    () => [
      { key: 'operativi', label: 'KPI Operativi' },
      { key: 'qualita', label: 'KPI Qualità Dati' },
      { key: 'verifica', label: 'KPI Verifica Clienti' },
      { key: 'funnel', label: 'KPI Funnel/Business' },
      { key: 'trend_annuale', label: 'Trend Annuale' }
    ],
    []
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.pipeline) count += 1;
    if (filters.data_inserimento_dal) count += 1;
    if (filters.data_inserimento_al) count += 1;
    return count;
  }, [filters.data_inserimento_al, filters.data_inserimento_dal, filters.pipeline]);

  const selectedPipelineLabel = useMemo(() => {
    if (!filters.pipeline) return '';
    const found = pipelineOptions.find((item) => item.id === String(filters.pipeline));
    return found?.nome || filters.pipeline;
  }, [filters.pipeline, pipelineOptions]);

  const handleResetFilters = () => {
    setFilters({
      data_inserimento_dal: getDefaultDateFrom(),
      data_inserimento_al: getDefaultDateTo(),
      pipeline: ''
    });
  };

  const annualHasDataForMode = useMemo(() => {
    if (!snapshot?.annual?.annualTrend) return false;
    if (trendMode === 'import_only') {
      return snapshot.annual.annualTrend.some((row) => Number(row?.imported || 0) > 0);
    }
    if (trendMode === 'value_conversion') {
      return snapshot.annual.annualTrend.some(
        (row) => Number(row?.revenue || 0) > 0 || Number(row?.conversion || 0) > 0
      );
    }
    return Boolean(snapshot.annual.annualHasAnyData);
  }, [snapshot, trendMode]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <BarChartLogoIcon size={20} color="#4f46e5" />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Funnelix KPI
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dashboard KPI operativi, qualità dati, verifica clienti e funnel/business
          </Typography>
        </Box>
      </Box>

      <StyledCard>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Data inserimento dal"
                InputLabelProps={{ shrink: true }}
                value={filters.data_inserimento_dal}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    data_inserimento_dal: e.target.value
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Data inserimento al"
                InputLabelProps={{ shrink: true }}
                value={filters.data_inserimento_al}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    data_inserimento_al: e.target.value
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Pipeline</InputLabel>
                <Select
                  value={filters.pipeline}
                  label="Pipeline"
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      pipeline: e.target.value
                    }))
                  }
                >
                  <MenuItem value="">Tutte</MenuItem>
                  {pipelineOptions.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button fullWidth variant="outlined" onClick={handleResetFilters}>
                Reset filtri
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Filtri attivi
                </Typography>
                {filters.data_inserimento_dal && (
                  <Chip
                    size="small"
                    label={`Da: ${formatYmdToIt(filters.data_inserimento_dal)}`}
                    onDelete={() => setFilters((prev) => ({ ...prev, data_inserimento_dal: '' }))}
                  />
                )}
                {filters.data_inserimento_al && (
                  <Chip
                    size="small"
                    label={`A: ${formatYmdToIt(filters.data_inserimento_al)}`}
                    onDelete={() => setFilters((prev) => ({ ...prev, data_inserimento_al: '' }))}
                  />
                )}
                {filters.pipeline && (
                  <Chip
                    size="small"
                    label={`Pipeline: ${selectedPipelineLabel}`}
                    onDelete={() => setFilters((prev) => ({ ...prev, pipeline: '' }))}
                  />
                )}
                {!activeFilterCount && (
                  <Typography variant="caption" color="text.secondary">
                    Nessun filtro applicato
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ mb: 2 }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.key} value={tab.key} label={tab.label} />
            ))}
          </Tabs>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && !snapshot && (
            <Alert severity="info">Nessun dato KPI disponibile al momento.</Alert>
          )}

          {!loading && snapshot && activeTab === 'operativi' && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Contatti importati (totale)"
                  value={formatNumber(snapshot.totals.totaleContatti)}
                  subtitle="Base cumulata in funnelix_contatti"
                  tooltip={`Formula: COUNT(*)\nTabella: funnelix_contatti`}
                  icon={Users}
                  iconBg="#eef2ff"
                  iconColor="#4f46e5"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Tasso successo sync"
                  value={formatPercent(snapshot.operativi.syncSuccessRate)}
                  subtitle={`Esiti OK: ${snapshot.operativi.syncOk} / ${snapshot.operativi.syncTotale}`}
                  tooltip={`Formula: sync_ok / sync_totali * 100\nTabella: funnelix_log_sync`}
                  icon={ShieldCheck}
                  iconBg="#ecfdf3"
                  iconColor="#059669"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Error rate sync"
                  value={formatPercent(snapshot.operativi.syncErrorRate)}
                  subtitle={`Sync in errore: ${snapshot.operativi.syncKo}`}
                  tooltip={`Formula: sync_errore / sync_totali * 100\nTabella: funnelix_log_sync`}
                  icon={ShieldAlert}
                  iconBg="#fef2f2"
                  iconColor="#dc2626"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <MetricTitle
                      title="Ultimo import completato"
                      tooltip={`Campo: last_successful_import_at\nFonte: endpoint /import/contatti/last-successful`}
                    />
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {formatDateTime(snapshot.operativi.lastImportAt)}
                    </Typography>
                    {(() => {
                      const tone = getFreshnessTone(snapshot.operativi.freshnessHours);
                      return (
                        <Box
                          sx={{
                            mt: 1,
                            display: 'inline-flex',
                            px: 1,
                            py: 0.35,
                            borderRadius: 999,
                            bgcolor: tone.bg
                          }}
                        >
                          <Typography variant="caption" sx={{ color: tone.color, fontWeight: 600 }}>
                            {snapshot.operativi.freshnessHours == null
                              ? 'Tempo dall\'ultimo import: non disponibile'
                              : `Tempo dall'ultimo import: ${formatRelativeAge(snapshot.operativi.freshnessHours)}`}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </CardContent>
                </StyledCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <MetricTitle
                      title="Stato operativo integrazione"
                      tooltip={`Indicatori sintetici dai log di sincronizzazione.\nTabella: funnelix_log_sync`}
                    />
                    <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip color="success" label={`Sync OK: ${snapshot.operativi.syncOk}`} />
                      <Chip color="error" label={`Sync KO: ${snapshot.operativi.syncKo}`} />
                      <Chip variant="outlined" label={`Totale log letti: ${snapshot.operativi.syncTotale}`} />
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {!loading && snapshot && activeTab === 'qualita' && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Copertura pipeline"
                  value={formatPercent(snapshot.qualita.coperturaPipeline)}
                  subtitle={`Senza pipeline: ${formatNumber(snapshot.qualita.totalePipelineMissing)}`}
                  tooltip={`Formula: (contatti_con_pipeline / contatti_totali) * 100\nTabella: funnelix_contatti`}
                  icon={Database}
                  iconBg="#eff6ff"
                  iconColor="#2563eb"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Copertura stage"
                  value={formatPercent(snapshot.qualita.coperturaStage)}
                  subtitle={`Senza stage: ${formatNumber(snapshot.qualita.totaleStageMissing)}`}
                  tooltip={`Formula: (contatti_con_stage / contatti_totali) * 100\nTabella: funnelix_contatti`}
                  icon={Gauge}
                  iconBg="#ecfeff"
                  iconColor="#0e7490"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Record da arricchire"
                  value={formatNumber(Math.max(snapshot.qualita.totalePipelineMissing, snapshot.qualita.totaleStageMissing))}
                  subtitle="Contatti con metadati commerciali mancanti"
                  tooltip={`Formula: max(record_senza_pipeline, record_senza_stage)\nTabelle: funnelix_contatti, funnelix_pipeline, funnelix_pipeline_stati`}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Stato qualità dati
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      La copertura misura la presenza dei campi materializzati correnti (`funnelix_pipeline_id_current`,
                      `funnelix_stage_id_current`) sui contatti importati.
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {!loading && snapshot && activeTab === 'verifica' && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Match rate clienti"
                  value={formatPercent(snapshot.verifica.matchRate)}
                  subtitle={`${formatNumber(snapshot.totals.totaleClienti)} clienti trovati`}
                  tooltip={`Formula: clienti_trovati / contatti_totali * 100\nTabelle: funnelix_contatti, funnelix_verifiche_clienti`}
                  icon={TrendingUp}
                  iconBg="#ecfdf3"
                  iconColor="#059669"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Non clienti"
                  value={formatPercent(snapshot.verifica.nonClientRate)}
                  subtitle={`${formatNumber(snapshot.totals.totaleNonClienti)} contatti non clienti`}
                  tooltip={`Formula: non_clienti / contatti_totali * 100\nTabelle: funnelix_contatti, funnelix_verifiche_clienti`}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Lead -> Cliente"
                  value={formatPercent(snapshot.verifica.conversionLeadCliente)}
                  subtitle="Conversione complessiva sul periodo 12 mesi"
                  tooltip={`Formula: SUM(cliente_trovato=1) / COUNT(*) * 100\nTabella: funnelix_contatti`}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Contatti da verificare"
                  value={formatNumber(snapshot.totals.totaleDaVerificare)}
                  subtitle="Backlog verifiche cliente"
                  tooltip={`Formula: COUNT(stato_verifica_cliente='da_verificare')\nTabella: funnelix_contatti`}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Verifiche in errore"
                  value={formatNumber(snapshot.totals.totaleErrore)}
                  subtitle="Record con stato verifica = errore"
                  tooltip={`Formula: COUNT(stato_verifica_cliente='errore')\nTabella: funnelix_contatti`}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Ticket medio clienti"
                  value={formatCurrency(snapshot.verifica.ticketMedioClienti)}
                  subtitle="Media importo contratto sui clienti verificati"
                  tooltip={`Formula: AVG(importo_contratto)\nFiltro: stato_verifica_cliente='verificato_cliente'\nTabella: funnelix_contatti`}
                />
              </Grid>
              <Grid item xs={12}>
                <KpiCard
                  title="Valore economico clienti"
                  value={formatCurrency(snapshot.verifica.fatturatoClienti)}
                  subtitle="Somma importi contratto (orizzonte 12 mesi)"
                  tooltip={`Formula: SUM(importo_contratto)\nFiltro: stato_verifica_cliente='verificato_cliente'\nTabella: funnelix_contatti`}
                  icon={CircleDollarSign}
                  iconBg="#eff6ff"
                  iconColor="#2563eb"
                />
              </Grid>
            </Grid>
          )}

          {!loading && snapshot && activeTab === 'funnel' && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <MetricTitle
                      title="Volume contatti per pipeline"
                      tooltip={`Formula: COUNT(*) GROUP BY pipeline corrente\nTabelle: funnelix_contatti, funnelix_pipeline`}
                    />
                    {snapshot.funnel.topPipeline.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Nessun dato disponibile.
                      </Typography>
                    )}
                    {snapshot.funnel.topPipeline.map((item) => {
                      const value = Number(item?.totale || 0);
                      const pct = snapshot.totals.totaleContatti > 0
                        ? (value / snapshot.totals.totaleContatti) * 100
                        : 0;
                      return (
                        <Box key={`${item?.funnelix_pipeline_id || 'none'}-${item?.nome}`} sx={{ mb: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">{item?.nome || 'Senza pipeline'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatNumber(value)}
                            </Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, pct))} />
                        </Box>
                      );
                    })}
                  </CardContent>
                </StyledCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardContent>
                    <MetricTitle
                      title="Pipeline con più clienti trovati"
                      tooltip={`Formula: COUNT(*) con cliente_trovato=1 GROUP BY pipeline\nTabelle: funnelix_contatti, funnelix_verifiche_clienti`}
                    />
                    {snapshot.funnel.topPipelineClienti.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Nessun dato disponibile.
                      </Typography>
                    )}
                    {snapshot.funnel.topPipelineClienti.map((item) => (
                      <Box key={`${item?.funnelix_pipeline_id || 'none'}-${item?.nome}`} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">{item?.nome || 'Senza pipeline'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatNumber(item?.totale || 0)} clienti
                          </Typography>
                        </Box>
                        <Divider sx={{ mt: 0.75 }} />
                      </Box>
                    ))}
                  </CardContent>
                </StyledCard>
              </Grid>
              <Grid item xs={12}>
                <StyledCard>
                  <CardContent>
                    <MetricTitle
                      title="Quota clienti per stage"
                      tooltip={`Formula: clienti_per_stage / contatti_per_stage * 100\nTabelle: funnelix_contatti, funnelix_pipeline_stati`}
                    />
                    <Grid container spacing={1}>
                      {snapshot.funnel.quotaClientiPerStage.map((item) => (
                        <Grid item xs={12} md={6} key={item.nome}>
                          <Box sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {item.nome}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Clienti {formatNumber(item.clienti)} su {formatNumber(item.totale)} contatti
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {formatPercent(item.quota)}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {!loading && snapshot && activeTab === 'trend_annuale' && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Picco Import Mensile"
                  value={formatNumber(snapshot.annual.annualPeakImports)}
                  subtitle="Massimo contatti importati in un mese (ultimi 12)"
                  icon={Activity}
                  iconBg="#eef2ff"
                  iconColor="#4f46e5"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Picco Valore Mensile"
                  value={formatCurrency(snapshot.annual.annualPeakRevenue)}
                  subtitle="Massimo valore clienti in un mese"
                  icon={CircleDollarSign}
                  iconBg="#eff6ff"
                  iconColor="#2563eb"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <KpiCard
                  title="Conversione Media 12M"
                  value={formatPercent(snapshot.annual.annualAvgConversion)}
                  subtitle="Media mensile lead -> cliente (ultimi 12 mesi)"
                  icon={TrendingUp}
                  iconBg="#ecfdf3"
                  iconColor="#059669"
                />
              </Grid>
              <Grid item xs={12}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Trend Annuale Impatto
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Confronto mensile tra conversione lead-clienti e valore economico clienti (ultimi 12 mesi).
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      <Button
                        size="small"
                        variant={trendMode === 'all' ? 'contained' : 'outlined'}
                        onClick={() => setTrendMode('all')}
                      >
                        Tutti i KPI
                      </Button>
                      <Button
                        size="small"
                        variant={trendMode === 'import_only' ? 'contained' : 'outlined'}
                        onClick={() => setTrendMode('import_only')}
                      >
                        Solo Import
                      </Button>
                      <Button
                        size="small"
                        variant={trendMode === 'value_conversion' ? 'contained' : 'outlined'}
                        onClick={() => setTrendMode('value_conversion')}
                      >
                        Solo Valore+Conversione
                      </Button>
                    </Box>
                    {!annualHasDataForMode ? (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Nessun dato disponibile per la vista selezionata. Prova a cambiare vista o ampliare i filtri.
                      </Alert>
                    ) : (
                      <Box sx={{ width: '100%', height: 420 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={snapshot.annual.annualTrend} margin={{ top: 12, right: 20, left: 0, bottom: 8 }}>
                            <defs>
                              <linearGradient id="annualRevenueFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#2563eb" stopOpacity={0.35} />
                                <stop offset="95%" stopColor="#2563eb" stopOpacity={0.04} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                            <XAxis dataKey="monthLabel" tick={{ fontSize: 12 }} />
                            <YAxis
                              yAxisId="left"
                              tick={{ fontSize: 12 }}
                              width={55}
                            />
                            {trendMode !== 'import_only' && (
                              <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fontSize: 12 }}
                                unit="%"
                                width={50}
                              />
                            )}
                            <RechartsTooltip
                              formatter={(value, name, item) => {
                                if (name === 'Valore Clienti (kEUR)') {
                                  return formatCurrency(item?.payload?.revenue || 0);
                                }
                                if (name === 'Conversione Lead->Cliente') return formatPercent(value);
                                if (name === 'Contatti Importati') return formatNumber(value);
                                return value;
                              }}
                              contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0' }}
                            />
                            <Legend />
                            {trendMode !== 'value_conversion' && (
                              <Bar
                                yAxisId="left"
                                dataKey="imported"
                                name="Contatti Importati"
                                fill="#6366f1"
                                radius={[6, 6, 0, 0]}
                                barSize={18}
                              />
                            )}
                            {trendMode !== 'import_only' && (
                              <Area
                                yAxisId="left"
                                type="monotone"
                                dataKey="revenueK"
                                name="Valore Clienti (kEUR)"
                                stroke="#2563eb"
                                fill="url(#annualRevenueFill)"
                                strokeWidth={2.2}
                              />
                            )}
                            {trendMode !== 'import_only' && (
                              <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="conversion"
                                name="Conversione Lead->Cliente"
                                stroke="#059669"
                                strokeWidth={3}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                              />
                            )}
                          </ComposedChart>
                        </ResponsiveContainer>
                      </Box>
                    )}
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default FunnelixKPI;
